import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  listStoreSlotsWithTokenApi,
  StoreSlotWithAvailability,
} from "../../../views/scheduling/schedulingApi";

import type { StoreForEvaluation, EvaluationFreelancerCandidate } from "./SelectFreelancersForEvaluationModal";
import type { SubmittedFreelancerEvaluationPublic } from "../../../views/supervisor/supervisorApi";

import EcoIcon from "../../eco-icon/EcoIcon";
import EcoPagination from "../../eco-ui/EcoPagination";
import "./ViewSubmittedEvaluationsModal.css";

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatBrDate(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function startOfWeekMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function buildIsoUtc(dayYmd: string, hhmm: string): string {
  return new Date(`${dayYmd}T${hhmm}:00`).toISOString();
}

function formatTimeFromIso(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function ymdFromIsoOrYmd(s: string): string {
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function formatSubmittedAt(iso?: string | null) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

type Step = "STORE" | "SLOTS" | "FREELANCERS" | "EVALUATIONS";
type FetchStoresPageResult = { items: StoreForEvaluation[]; total: number };

type Props = {
  isOpen: boolean;
  onClose: () => void;

  token: string;

  fetchStoresPage: (args: {
    token: string;
    page: number;
    limit: number;
  }) => Promise<FetchStoresPageResult>;

  fetchFreelancersForEvaluation: (args: {
    token: string;
    storeId: string;
    dayYmd: string;
    slotId: string;
  }) => Promise<EvaluationFreelancerCandidate[]>;

  fetchSubmittedEvaluationsByRegistration: (args: {
    token: string;
    registrationId: string;
  }) => Promise<SubmittedFreelancerEvaluationPublic[]>;
};

const STORES_LIMIT = 10;

const STEPS: { key: Step; label: string }[] = [
  { key: "STORE", label: "Loja" },
  { key: "SLOTS", label: "Horário" },
  { key: "FREELANCERS", label: "Freelancer" },
  { key: "EVALUATIONS", label: "Avaliações" },
];

/** Status da inscrição no horário (`SlotRegistrationStatus`). */
const PRESENCE_LABELS: Record<string, string> = {
  PENDENTE: "Inscrição pendente",
  APROVADO: "Aprovado",
  REJEITADO: "Rejeitado",
  FALTOU: "Faltou",
  AUSENTE: "Ausente",
  CANCELADO: "Cancelado",
};

const ViewSubmittedEvaluationsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  token,
  fetchStoresPage,
  fetchFreelancersForEvaluation,
  fetchSubmittedEvaluationsByRegistration,
}) => {
  const [step, setStep] = useState<Step>("STORE");

  const [storesPage, setStoresPage] = useState(1);
  const [storesTotal, setStoresTotal] = useState(0);
  const [stores, setStores] = useState<StoreForEvaluation[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [selectedStore, setSelectedStore] = useState<StoreForEvaluation | null>(null);

  const fetchStores = useCallback(
    async (page: number) => {
      if (!token) return;

      try {
        setLoadingStores(true);
        const data = await fetchStoresPage({ token, page, limit: STORES_LIMIT });
        setStores(data.items ?? []);
        setStoresTotal(data.total ?? 0);
      } catch (err: any) {
        toast.error(err?.message ?? "Erro ao buscar lojas.");
      } finally {
        setLoadingStores(false);
      }
    },
    [token, fetchStoresPage],
  );

  const [weekAnchor, setWeekAnchor] = useState<Date>(() => new Date());
  const weekStart = useMemo(() => startOfWeekMonday(weekAnchor), [weekAnchor]);

  const daysOfWeek = useMemo(() => {
    const labels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
    return labels.map((label, idx) => {
      const d = addDays(weekStart, idx);
      return { label, date: d, ymd: toYmd(d) };
    });
  }, [weekStart]);

  const [selectedDayYmd, setSelectedDayYmd] = useState<string>(() => toYmd(new Date()));
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [weekSlots, setWeekSlots] = useState<StoreSlotWithAvailability[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<StoreSlotWithAvailability | null>(null);

  const fetchWeekSlots = useCallback(async () => {
    if (!token) return;
    if (!selectedStore?.id) return;

    try {
      setLoadingSlots(true);

      const weekStartYmd = toYmd(weekStart);
      const weekEndYmd = toYmd(addDays(weekStart, 7));

      const from = buildIsoUtc(weekStartYmd, "00:00");
      const to = buildIsoUtc(weekEndYmd, "00:00");

      const slots = await listStoreSlotsWithTokenApi(selectedStore.id, token, {
        from,
        to,
        onlyAvailable: false,
        includeCanceled: false,
      });

      const sorted = [...(slots ?? [])].sort((a, b) => a.startAt.localeCompare(b.startAt));
      setWeekSlots(sorted);
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao listar horários.");
    } finally {
      setLoadingSlots(false);
    }
  }, [token, selectedStore?.id, weekStart]);

  const slotsByDay = useMemo(() => {
    const map: Record<string, StoreSlotWithAvailability[]> = {};
    for (const s of weekSlots) {
      const dayYmd = ymdFromIsoOrYmd(s.day);
      if (!map[dayYmd]) map[dayYmd] = [];
      map[dayYmd].push(s);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.startAt.localeCompare(b.startAt));
    return map;
  }, [weekSlots]);

  const selectedDaySlots = useMemo(() => slotsByDay[selectedDayYmd] ?? [], [slotsByDay, selectedDayYmd]);

  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidates, setCandidates] = useState<EvaluationFreelancerCandidate[]>([]);

  const [loadingEvaluations, setLoadingEvaluations] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<EvaluationFreelancerCandidate | null>(null);
  const [evaluations, setEvaluations] = useState<SubmittedFreelancerEvaluationPublic[]>([]);

  const busy = loadingStores || loadingSlots || loadingCandidates || loadingEvaluations;

  const goToCandidates = useCallback(
    async (slot: StoreSlotWithAvailability) => {
      if (!token) return;
      if (!selectedStore?.id) return;

      const dayYmd = ymdFromIsoOrYmd(slot.day);

      setSelectedSlot(slot);
      setStep("FREELANCERS");
      setCandidates([]);
      setSelectedCandidate(null);
      setEvaluations([]);

      try {
        setLoadingCandidates(true);
        const list = await fetchFreelancersForEvaluation({
          token,
          storeId: selectedStore.id,
          dayYmd,
          slotId: slot.id,
        });
        setCandidates(list ?? []);
      } catch (err: any) {
        toast.error(err?.message ?? "Erro ao listar freelancers cadastrados.");
      } finally {
        setLoadingCandidates(false);
      }
    },
    [token, selectedStore?.id, fetchFreelancersForEvaluation],
  );

  const goToEvaluations = useCallback(
    async (c: EvaluationFreelancerCandidate) => {
      if (!token) return;
      if (!c.registrationId) return;

      setSelectedCandidate(c);
      setStep("EVALUATIONS");
      setEvaluations([]);

      try {
        setLoadingEvaluations(true);
        const list = await fetchSubmittedEvaluationsByRegistration({ token, registrationId: c.registrationId });
        setEvaluations((list ?? []).filter((e) => Boolean(e.submittedAt)));
      } catch (err: any) {
        toast.error(err?.message ?? "Erro ao listar avaliações.");
      } finally {
        setLoadingEvaluations(false);
      }
    },
    [token, fetchSubmittedEvaluationsByRegistration],
  );

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, busy]);

  useEffect(() => {
    if (!isOpen) return;

    if (!token) {
      toast.error("Sessão inválida. Faça login novamente.");
      onClose();
      return;
    }

    setStep("STORE");

    setStoresPage(1);
    setStoresTotal(0);
    setStores([]);
    setSelectedStore(null);

    setWeekAnchor(new Date());
    setSelectedDayYmd(toYmd(new Date()));
    setWeekSlots([]);
    setSelectedSlot(null);

    setCandidates([]);
    setSelectedCandidate(null);
    setEvaluations([]);

    setLoadingCandidates(false);
    setLoadingEvaluations(false);

    fetchStores(1);
  }, [isOpen, token, onClose, fetchStores]);

  useEffect(() => {
    if (!isOpen) return;
    if (step !== "STORE") return;
    fetchStores(storesPage);
  }, [isOpen, step, storesPage, fetchStores]);

  useEffect(() => {
    if (!isOpen) return;
    if (step !== "SLOTS") return;
    fetchWeekSlots();
  }, [isOpen, step, fetchWeekSlots]);

  useEffect(() => {
    if (!isOpen) return;
    if (step !== "SLOTS") return;

    const inWeek = daysOfWeek.some((d) => d.ymd === selectedDayYmd);
    if (!inWeek) setSelectedDayYmd(daysOfWeek[0]?.ymd ?? toYmd(new Date()));
  }, [isOpen, step, daysOfWeek, selectedDayYmd]);

  const handleOverlayClick = () => {
    if (!busy) onClose();
  };

  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };

  const renderAnswer = (a: any) => {
    const q = a?.question;
    if (!q) return "-";
    if (q.type === "BOOLEAN") return a.booleanValue === true ? "Sim" : a.booleanValue === false ? "Não" : "-";
    if (q.type === "SCALE") return a.intValue != null ? String(a.intValue) : "-";
    if (q.type === "TEXT") return typeof a.textValue === "string" && a.textValue.trim() ? a.textValue : "-";
    if (q.type === "SINGLE_CHOICE") {
      const opt = (q.options ?? []).find((o: any) => o.id === a.optionId);
      return opt?.label ?? "-";
    }
    if (q.type === "MULTI_CHOICE") {
      const ids: string[] = Array.isArray(a.optionIds) ? a.optionIds : [];
      const labels = ids
        .map((id) => (q.options ?? []).find((o: any) => o.id === id)?.label)
        .filter(Boolean);
      return labels.length ? labels.join(", ") : "-";
    }
    return "-";
  };

  if (!isOpen) return null;

  const stepIndex = STEPS.findIndex((s) => s.key === step);

  const backTo = (target: Step) => {
    if (busy) return;
    setStep(target);
    if (target === "STORE") {
      setSelectedStore(null);
      setWeekSlots([]);
    }
    if (target === "STORE" || target === "SLOTS") {
      setSelectedSlot(null);
      setCandidates([]);
    }
    setSelectedCandidate(null);
    setEvaluations([]);
  };

  const previousStep: Step | null = step === "SLOTS" ? "STORE" : step === "FREELANCERS" ? "SLOTS" : step === "EVALUATIONS" ? "FREELANCERS" : null;

  const slotLabel = (s: StoreSlotWithAvailability) => {
    const endIsNextDay = s.endAt.slice(0, 10) !== s.startAt.slice(0, 10);
    return `${formatTimeFromIso(s.startAt)} – ${formatTimeFromIso(s.endAt)}${endIsNextDay ? " (+1)" : ""}`;
  };

  return (
    <div className="ecoDialog__overlay" onClick={handleOverlayClick} role="presentation">
      <div
        className="ecoDialog vseDialog"
        onClick={handleModalClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vse-title"
      >
        <header className="vseDialog__header">
          <div>
            <h2 id="vse-title" className="ecoDialog__title">
              Visualizar avaliações
            </h2>
            <ol className="vseSteps" aria-label="Etapas">
              {STEPS.map((s, i) => (
                <li
                  key={s.key}
                  className={`vseSteps__item${i === stepIndex ? " is-current" : ""}${i < stepIndex ? " is-done" : ""}`}
                  aria-current={i === stepIndex ? "step" : undefined}
                >
                  {s.label}
                </li>
              ))}
            </ol>
          </div>
          <button
            type="button"
            className="ecoBtn ecoBtn--icon vseDialog__close"
            onClick={onClose}
            disabled={busy}
            aria-label="Fechar"
          >
            <EcoIcon name="x" size={18} />
          </button>
        </header>

        {/* Contexto do que já foi escolhido */}
        {step !== "STORE" ? (
          <dl className="vseContext">
            <div>
              <dt>Loja</dt>
              <dd>{selectedStore?.nomeFantasia ?? "—"}</dd>
            </div>
            {step === "SLOTS" ? (
              <div>
                <dt>Endereço</dt>
                <dd>{selectedStore?.endereco || "Não informado"}</dd>
              </div>
            ) : null}
            {selectedSlot ? (
              <div>
                <dt>Horário</dt>
                <dd>
                  {formatBrDate(ymdFromIsoOrYmd(selectedSlot.day))}, {slotLabel(selectedSlot)}
                </dd>
              </div>
            ) : null}
            {selectedCandidate ? (
              <div>
                <dt>Freelancer</dt>
                <dd>
                  {selectedCandidate.fullName} <span className="vseMuted">· CPF {selectedCandidate.cpf}</span>
                </dd>
              </div>
            ) : null}
          </dl>
        ) : null}

        <div className="vseDialog__body">
          {step === "STORE" ? (
            <section aria-labelledby="vse-lojas">
              <h3 id="vse-lojas" className="vseSection__title">
                Selecione uma loja
              </h3>
              {loadingStores ? (
                <p className="ecoEmpty">Carregando lojas...</p>
              ) : stores.length === 0 ? (
                <p className="ecoEmpty">Nenhuma loja encontrada.</p>
              ) : (
                <ul className="vseList">
                  {stores.map((store) => (
                    <li key={store.id}>
                      <button
                        type="button"
                        className="vseRow"
                        disabled={busy}
                        onClick={() => {
                          if (busy) return;
                          setSelectedStore(store);
                          setStep("SLOTS");

                          setWeekAnchor(new Date());
                          setSelectedDayYmd(toYmd(new Date()));
                          setWeekSlots([]);
                          setSelectedSlot(null);

                          setCandidates([]);
                          setSelectedCandidate(null);
                          setEvaluations([]);
                        }}
                      >
                        <span className="vseRow__text">
                          <span className="vseRow__title">{store.nomeFantasia}</span>
                          <span className="vseRow__sub">
                            {store.endereco ? store.endereco : "Endereço não informado"}
                            {store.estado ? ` · ${store.estado}` : ""}
                          </span>
                        </span>
                        <EcoIcon name="chevronRight" size={18} className="vseRow__chevron" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="vsePager">
                <EcoPagination
                  page={storesPage}
                  pageSize={STORES_LIMIT}
                  total={storesTotal}
                  onChange={setStoresPage}
                  disabled={loadingStores}
                />
              </div>
            </section>
          ) : null}

          {step === "SLOTS" ? (
            <section aria-labelledby="vse-horarios">
              <div className="vseWeek">
                <button
                  type="button"
                  className="ecoBtn ecoBtn--icon ecoBtn--sm"
                  onClick={() => setWeekAnchor((d) => addDays(d, -7))}
                  disabled={loadingSlots}
                  aria-label="Semana anterior"
                >
                  <EcoIcon name="chevronLeft" size={18} />
                </button>
                <h3 id="vse-horarios" className="vseWeek__title">
                  Semana de {formatBrDate(toYmd(weekStart))}
                </h3>
                <button
                  type="button"
                  className="ecoBtn ecoBtn--icon ecoBtn--sm"
                  onClick={() => setWeekAnchor((d) => addDays(d, 7))}
                  disabled={loadingSlots}
                  aria-label="Próxima semana"
                >
                  <EcoIcon name="chevronRight" size={18} />
                </button>
              </div>

              <div className="vseDays" role="group" aria-label="Dia da semana">
                {daysOfWeek.map((d) => {
                  const active = d.ymd === selectedDayYmd;
                  const count = (slotsByDay[d.ymd] ?? []).length;
                  return (
                    <button
                      key={d.ymd}
                      type="button"
                      className={`vseDay${active ? " is-selected" : ""}`}
                      aria-pressed={active}
                      onClick={() => setSelectedDayYmd(d.ymd)}
                      disabled={loadingSlots || count === 0}
                      title={count === 0 ? "Sem horários neste dia" : undefined}
                    >
                      <span className="vseDay__label">{d.label}</span>
                      <span className="vseDay__date">{formatBrDate(d.ymd).slice(0, 5)}</span>
                      <span className="vseDay__count">{count === 0 ? "—" : `${count} horário${count === 1 ? "" : "s"}`}</span>
                    </button>
                  );
                })}
              </div>

              <h4 className="vseSection__title vseSection__title--sub">Horários de {formatBrDate(selectedDayYmd)}</h4>
              {loadingSlots ? (
                <p className="ecoEmpty">Carregando horários...</p>
              ) : selectedDaySlots.length === 0 ? (
                <p className="ecoEmpty">Selecione um dia que tenha horários.</p>
              ) : (
                <ul className="vseList">
                  {selectedDaySlots.map((s) => (
                    <li key={s.id}>
                      <button type="button" className="vseRow" onClick={() => goToCandidates(s)} disabled={busy}>
                        <span className="vseRow__text">
                          <span className="vseRow__title">
                            {slotLabel(s)}
                            {s.isCanceled ? <span className="ecoPill ecoPill--off vseRow__pill">Cancelado</span> : null}
                            {s.isFull ? <span className="ecoPill vseRow__pill">Lotado</span> : null}
                          </span>
                          <span className="vseRow__sub">
                            {s.available} de {s.totalCapacity} vagas livres · {s.occupied} ocupada{s.occupied === 1 ? "" : "s"}
                          </span>
                        </span>
                        <EcoIcon name="chevronRight" size={18} className="vseRow__chevron" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          {step === "FREELANCERS" ? (
            <section aria-labelledby="vse-freelancers">
              <h3 id="vse-freelancers" className="vseSection__title">
                Freelancers cadastrados no horário
              </h3>
              {loadingCandidates ? (
                <p className="ecoEmpty">Carregando freelancers...</p>
              ) : candidates.length === 0 ? (
                <p className="ecoEmpty">Nenhum freelancer encontrado para este dia e horário.</p>
              ) : (
                <ul className="vseList">
                  {candidates.map((c) => (
                    <li key={c.registrationId}>
                      <button type="button" className="vseRow" onClick={() => goToEvaluations(c)} disabled={busy}>
                        <span className="vseRow__text">
                          <span className="vseRow__title">{c.fullName}</span>
                          <span className="vseRow__sub">
                            CPF {c.cpf} · {PRESENCE_LABELS[c.presenceStatus] ?? c.presenceStatus}
                          </span>
                        </span>
                        <EcoIcon name="chevronRight" size={18} className="vseRow__chevron" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          {step === "EVALUATIONS" ? (
            <section aria-labelledby="vse-avaliacoes">
              <h3 id="vse-avaliacoes" className="vseSection__title">
                Avaliações enviadas
              </h3>
              {loadingEvaluations ? (
                <p className="ecoEmpty">Carregando avaliações...</p>
              ) : evaluations.length === 0 ? (
                <p className="ecoEmpty">Nenhuma avaliação enviada para este freelancer.</p>
              ) : (
                <div className="vseEvals">
                  {evaluations.map((ev) => (
                    <article key={ev.id} className="vseEval">
                      <header className="vseEval__head">
                        <span className="vseEval__form">{ev.form?.formType?.name ?? "Formulário"}</span>
                        <span className="vseMuted">Enviada em {formatSubmittedAt(ev.submittedAt)}</span>
                      </header>
                      <dl className="vseAnswers">
                        {(ev.answers ?? [])
                          .slice()
                          .sort((a: any, b: any) => (a.question?.order ?? 0) - (b.question?.order ?? 0))
                          .map((a: any) => (
                            <div key={a.questionId} className="vseAnswer">
                              <dt>{a.question?.prompt ?? "—"}</dt>
                              <dd>{renderAnswer(a)}</dd>
                            </div>
                          ))}
                      </dl>
                    </article>
                  ))}
                </div>
              )}
            </section>
          ) : null}
        </div>

        <div className="ecoDialog__actions">
          {previousStep ? (
            <button type="button" className="ecoBtn" onClick={() => backTo(previousStep)} disabled={busy}>
              <EcoIcon name="arrowLeft" size={18} />
              Voltar
            </button>
          ) : null}
          <button type="button" className="ecoBtn ecoBtn--primary" onClick={onClose} disabled={busy}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewSubmittedEvaluationsModal;
